import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { MusicalGenreViewStore } from "../../store";
import { MusicalGenreView } from "../view/MusicalGenreView";
import { MusicalGenreUpdateStore } from "../../store";
import { MusicalGenreUpdate } from "../update/MusicalGenreUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type MusicalGenreModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const MusicalGenreModalContent: React.FC<MusicalGenreModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new MusicalGenreUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new MusicalGenreViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <MusicalGenreView
            musicalGenreViewStore={storeInfo.store as MusicalGenreViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <MusicalGenreUpdate
            musicalGenreUpdateStore={storeInfo.store as MusicalGenreUpdateStore}
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
